import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { mcGetTokenAnalytics } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { Activity, Users, Lock } from 'lucide-react';
import { fmtUsd, shortAddr } from '../lib/numfmt';

const fmtBase = (v) => {
  const n = parseFloat(v);
  if (!isFinite(n) || n === 0) return '0';
  const a = Math.abs(n);
  if (a >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return n.toFixed(2);
};

function PressureBar({ pressure }) {
  const buys = pressure.buys || 0;
  const sells = pressure.sells || 0;
  const totalCt = buys + sells;
  const buyPct = totalCt ? (buys / totalCt) * 100 : 50;
  const bv = pressure.buy_volume_usd || 0;
  const sv = pressure.sell_volume_usd || 0;
  const totalVol = bv + sv;
  const buyVolPct = totalVol ? (bv / totalVol) * 100 : 50;
  const tone = pressure.sentiment === 'bullish' ? 'text-success border-success/50'
    : pressure.sentiment === 'bearish' ? 'text-destructive border-destructive/50'
    : 'text-muted-foreground border-border';
  return (
    <Card data-testid="mc-pressure-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between">
          <span className="flex items-center gap-2"><Activity className="w-4 h-4" /> Buy / Sell Pressure</span>
          <Badge variant="outline" className={`uppercase text-[10px] ${tone}`} data-testid="mc-sentiment">{pressure.sentiment}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="flex justify-between text-[11px] text-muted-foreground mb-1">
            <span>Trades (24h) · {buys} buys</span><span>{sells} sells</span>
          </div>
          <div className="h-2.5 w-full rounded-full overflow-hidden bg-destructive/30 flex">
            <div className="h-full bg-success" style={{ width: `${buyPct}%` }} data-testid="mc-pressure-count-bar" />
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">By count: <span className="font-mono text-foreground">{pressure.by_count}%</span></div>
        </div>
        <div>
          <div className="flex justify-between text-[11px] text-muted-foreground mb-1">
            <span>Volume · {fmtUsd(bv)} bought</span><span>{fmtUsd(sv)} sold</span>
          </div>
          <div className="h-2.5 w-full rounded-full overflow-hidden bg-destructive/30 flex">
            <div className="h-full bg-success" style={{ width: `${buyVolPct}%` }} data-testid="mc-pressure-vol-bar" />
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">By volume: <span className="font-mono text-foreground">{pressure.by_volume}%</span> <span className="opacity-60">(recent pool trades)</span></div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Buy/Sell pressure + recent top traders for the token page (any non-Krown chain).
 * Self-fetches the mc analytics endpoint (GeckoTerminal recent-trades window).
 */
export default function McTradeActivity({ chain, token, symbol, chainLabel }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const label = chainLabel || chain;

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      setData(await mcGetTokenAnalytics(chain, token));
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load trade activity');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [chain, token]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="mc-trade-activity-loading">
        <Skeleton className="h-48" /><Skeleton className="h-48" />
      </div>
    );
  }
  if (error || !data || !data.indexed) {
    return (
      <Card className="border-dashed" data-testid="mc-trade-activity-empty">
        <CardContent className="pt-6 text-sm text-muted-foreground flex items-center gap-2">
          <Lock className="w-4 h-4" /> {error || `No indexed trades to analyze on ${label}.`}
        </CardContent>
      </Card>
    );
  }

  const traders = data.top_traders || [];
  const sym = symbol || data.symbol || 'base';

  return (
    <div className="space-y-6" data-testid="mc-trade-activity">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PressureBar pressure={data.pressure} />
        <Card className="border-dashed" data-testid="mc-holder-graph-note">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-muted-foreground">
              <Lock className="w-4 h-4" /> Holder & Wallet Forensics
            </CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-muted-foreground leading-relaxed">
            Whale/holder tiers and wallet-relationship graphs need a full on-chain holder + transfer index.
            Public APIs on {label} only expose recent DEX trades, so those forensic views remain Krown-only.
            The top holders table below is sourced from the chain explorer where available.
          </CardContent>
        </Card>
      </div>

      <Card data-testid="mc-top-traders-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center justify-between">
            <span className="flex items-center gap-2"><Users className="w-4 h-4" /> Recent Top Traders</span>
            {data.trades_window && (
              <span className="text-[10px] text-muted-foreground font-normal" data-testid="mc-trades-window">
                from last {data.trades_window.count} pool trades
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {traders.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">No recent trades found for this token.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]" data-testid="mc-top-traders-table">
                <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">#</th>
                    <th className="text-left px-3 py-2 font-medium">Wallet</th>
                    <th className="text-right px-3 py-2 font-medium">Volume</th>
                    <th className="text-right px-3 py-2 font-medium">Trades</th>
                    <th className="text-right px-3 py-2 font-medium">Net {sym}</th>
                    <th className="text-left px-3 py-2 font-medium">Side</th>
                  </tr>
                </thead>
                <tbody>
                  {traders.map((t) => (
                    <tr key={t.address} className="border-b border-border/60 hover:bg-muted/30" data-testid={`mc-trader-row-${t.address}`}>
                      <td className="px-3 py-2 text-muted-foreground">{t.rank}</td>
                      <td className="px-3 py-2">
                        <Link to={`/explorer/${chain}/address/${t.address}`} data-testid={`mc-trader-link-${t.address}`} className="font-mono text-xs text-primary hover:underline">
                          {shortAddr(t.address)}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{fmtUsd(t.volume_usd)}</td>
                      <td className="px-3 py-2 text-right font-mono">
                        <span className="text-success">{t.buys}</span>/<span className="text-destructive">{t.sells}</span>
                      </td>
                      <td className={`px-3 py-2 text-right font-mono ${t.is_net_buyer ? 'text-success' : 'text-destructive'}`}>
                        {t.is_net_buyer ? '+' : ''}{fmtBase(t.net_base)}
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className={`text-[10px] ${t.is_net_buyer ? 'text-success border-success/50' : 'text-destructive border-destructive/50'}`}>
                          {t.is_net_buyer ? 'NET BUY' : 'NET SELL'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
